import classNames from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

import { deleteMediaAdvertiserLogo, uploadImage, uploadMediaAdvertiserLogo } from '@/services/mediaInfo.service';
import { Advertiser, MediaInfo } from '../types';
import AdvertiserLogoPrompt from './AdvertiserLogoPrompt/AdvertiserLogoPrompt';
import { AdvertiserLogoStyle } from './advertiserLogo.style';

type Props = {
  disabled?: boolean;
  mediaAdvertiser: Advertiser;
  mediaInfoDictionary: MediaInfo[];
  onMediaInfoDictionariesReload: () => void;
  user: UserInterface;
  userRoles: UserRoleInterface[];
};
type State = {
  showPrompt: boolean;
};

function AdvertiserLogo({
  disabled,
  mediaAdvertiser,
  mediaInfoDictionary,
  onMediaInfoDictionariesReload,
  user,
  userRoles
}: Props): JSX.Element | null {
  const { formatMessage } = useIntl();

  const [{ showPrompt }, setState] = useState<State>({
    showPrompt: false
  });

  const urlLogo = (() => {
    let url = '';
    mediaInfoDictionary.forEach(mediaGroup => {
      mediaGroup.agencies.forEach(mediaAgency => {
        mediaAgency.advertisers.forEach(m => {
          if (m._id === mediaAdvertiser._id) {
            url = m.logo ? `${taptap.config.staticsUrlBase}${m.logo}` : '';
          }
        });
      });
    });
    return url;
  })();

  const onInputChange = async (e: any) => {
    if (e.target.files.length) {
      const raw = e.target.files[0];
      const logoImage = await uploadImage(raw, user.id!);
      await uploadMediaAdvertiserLogo(mediaAdvertiser._id, logoImage.image);
      await onMediaInfoDictionariesReload();
    }
  };

  const togglePrompt = () => {
    setState(prevState => ({
      ...prevState,
      showPrompt: !prevState.showPrompt
    }));
  };

  const buttonText = urlLogo
    ? formatMessage({
        defaultMessage: 'Modify',
        id: 'advertiserSelector.uploadLogoAdvertiser.modifyButtonText'
      })
    : formatMessage({
        defaultMessage: 'Advertiser´s logo',
        id: 'advertiserSelector.uploadLogoAdvertiser.uploadLogoButtonText'
      });

  const label = urlLogo
    ? formatMessage({
        defaultMessage: 'Current advertiser logo that will be used within the SONATA tool.',
        id: 'advertiserSelector.uploadLogoAdvertiser.labelUpload'
      })
    : formatMessage({
        defaultMessage: 'Advertiser’s logo',
        id: 'advertiserSelector.uploadLogoAdvertiser.label'
      });

  const advertiserLogoPermission = checkUserResourcePermissions('advertiserLogo', user, userRoles);

  const handleAdvertiserLogoDelete = async () => {
    await deleteMediaAdvertiserLogo(mediaAdvertiser._id);
    onMediaInfoDictionariesReload();
  };

  if (advertiserLogoPermission) {
    return (
      <AdvertiserLogoStyle className="advertiserLogo">
        <div className="advertiserLogo__container">
          <label className={classNames('advertiserLogo__label', { 'advertiserLogo__label--disabled': disabled })}>
            {label}
          </label>
          <div className="uploadBox">
            <div className="advertiserLogo__buttonWrapper">
              {urlLogo && (
                <div className="advertiserLogo__preview">
                  <img alt="logoAdvertiser" className="advertiserLogo__image" src={urlLogo} />
                </div>
              )}
              <label htmlFor="upload-logo">
                <ButtonUI
                  className={`advertiserLogo__button${urlLogo ? '--uploaded' : ''}`}
                  disabled={disabled}
                  icon="fal fa-image"
                  iconPosition="left"
                  id={'advertiser_logo_button'}
                  shape="squareRounded"
                  size="small"
                  text={buttonText}
                  type="secondary"
                  visible
                />
              </label>
              {urlLogo && (
                <ButtonUI
                  className="advertiserLogo__deleteButton"
                  icon="far fa-trash"
                  iconPosition="left"
                  id={'advertiser_logo_delete_button'}
                  onClick={togglePrompt}
                  shape="link"
                  size="medium"
                  type="secondary"
                  visible
                />
              )}
            </div>
            <input
              id="upload-logo"
              name="upload-logo"
              onChange={onInputChange}
              style={{ display: 'none' }}
              type="file"
            />
          </div>
        </div>
        {showPrompt && (
          <AdvertiserLogoPrompt
            id="advertiserLogo__prompt"
            onClosePrompt={togglePrompt}
            onDeleteAdvertiserLogo={handleAdvertiserLogoDelete}
            showPrompt={showPrompt}
          />
        )}
      </AdvertiserLogoStyle>
    );
  }

  if (urlLogo) {
    return (
      <AdvertiserLogoStyle className="advertiserLogo">
        <div className="advertiserLogo__container">
          <label className="advertiserLogo__label">{label}</label>
          <div className="advertiserLogo__preview">
            <img alt="logoAdvertiser" className="advertiserLogo__image" src={urlLogo} />
          </div>
        </div>
      </AdvertiserLogoStyle>
    );
  }

  return null;
}

export default AdvertiserLogo;
